import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { navigate } from '@reach/router';
import { useEthers } from '@usedapp/core';

import AmountCrypto from '@bitcoin-portal/verse-web-components/dist/AmountCrypto';
import AmountFiat from '@bitcoin-portal/verse-web-components/dist/AmountFiat';
import Body from '@bitcoin-portal/verse-web-components/dist/Body';
import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import Coin from '@bitcoin-portal/verse-web-components/dist/Coin';
import Label from '@bitcoin-portal/verse-web-components/dist/Label';
import Title from '@bitcoin-portal/verse-web-components/dist/Title';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { CoinDisplay } from '@components/CoinDisplay';

import { DEFAULT_DECIMALS } from '@context/constants';
import { useDexRates } from '@context/providers/DexRatesProvider';
import { useFarms } from '@context/providers/FarmsProvider';
import { useLiquidityPairs } from '@context/providers/LiquidityPairsProvider';
import { useDispatch, useTrackedState } from '@context/store';

import { formatValue } from '@helpers/formatValue';
import { findProvider, lpTokenValue } from '@helpers/index';

import { useCustomBalance } from '@hooks/core';

import { MobileRowButton, PairName } from './styled';

interface Props {
  farm: IFarmPair;
  setFarm: (farm: IFarmPair) => void;
  onConnectWallet?: () => void;
}

const MobileRow: FunctionComponent<Props> = ({
  farm,
  setFarm,
  onConnectWallet,
}) => {
  const { account } = useEthers();
  const dispatch = useDispatch();
  const { farmEarned, farmBalances, farmTotalSupply } = useFarms();
  const { provider: exchangeProvider } = useTrackedState();
  const { liquidityPairs } = useLiquidityPairs();
  const dexRates = useDexRates();

  const provider = findProvider(exchangeProvider);
  const pair =
    liquidityPairs?.find(
      p => p.id.toLowerCase() === farm.pair?.id?.toLowerCase(),
    ) || farm.pair;
  const { rewardToken: reward } = farm;

  const tokenRate = farm.singleSided
    ? dexRates?.[exchangeProvider]?.[pair.token0?.symbol?.toUpperCase()] || 0
    : 0;

  const readableStakedBalance = parseFloat(
    formatValue(farmBalances[farm.contractAddress], DEFAULT_DECIMALS),
  );
  const earnedAmount = parseFloat(
    formatValue(farmEarned[farm.contractAddress], DEFAULT_DECIMALS),
  );
  const totalSupply = parseFloat(
    formatValue(farmTotalSupply[farm.contractAddress], DEFAULT_DECIMALS),
  );

  const lpTokenBalance = useCustomBalance(
    pair?.id,
    account || '',
    provider.supportedChains[0].chainId,
  );

  const readableLPTokenBalance = parseFloat(
    formatValue(lpTokenBalance, DEFAULT_DECIMALS),
  );

  const noLiquidity = !readableLPTokenBalance;
  const nothingStaked = !readableStakedBalance;
  const farmViewable = readableStakedBalance ? true : noLiquidity;

  const viewPair = (oldFarm: boolean) => {
    const hasStakedAmount = readableStakedBalance > 0;

    dispatch({
      type: 'SET_SELECTED_LIQUIDITY_PAIR',
      payload: pair,
    });

    const { chainParam } = provider || {
      chainParam: 'eth',
    };

    const pairParam = `${pair?.token0?.symbol.toLowerCase()}-${pair?.token1?.symbol.toLowerCase()}${
      oldFarm ? `-old` : ``
    }`;
    const routeString =
      !hasStakedAmount && !noLiquidity
        ? `/farms/${chainParam}/${pairParam}/deposit/`
        : `/farms/${chainParam}/${pairParam}/`;
    navigate(routeString, {
      state: { fromRoot: true, newTx: true },
    });
  };

  return (
    <MobileRowButton
      inactive={farm?.hidden}
      onClick={() => {
        if (!account && typeof onConnectWallet === 'function') {
          onConnectWallet();
        } else {
          setFarm(farm);
          viewPair(farm?.hidden === true);
        }
      }}
    >
      <div>
        <CoinDisplay farm={farm} />
        <div>
          <PairName>
            <Title>
              <span style={{ verticalAlign: 'middle' }}>
                {pair?.token0?.symbol}
                {!farm.singleSided && ` / ${pair?.token1?.symbol}`}
              </span>
              {farm?.hidden && (
                <span
                  style={{
                    fontSize: '12px',
                    marginLeft: '4px',
                  }}
                >
                  <FormattedMessage id="farms.list.inactive" />
                </span>
              )}
            </Title>
            <Body
              size="medium"
              style={{
                color: colors.shade80,
                paddingTop: '4px',
              }}
            >
              <AmountFiat
                amount={lpTokenValue(totalSupply, pair, tokenRate)}
                currency="USD"
                locale="en-US"
              />
            </Body>
          </PairName>
          <Button
            as="div"
            small
            design={farmViewable ? 'secondary' : 'primary'}
            style={{ width: 74 }}
          >
            {farmViewable ? (
              <FormattedMessage id="farms.global.viewButton" />
            ) : (
              <FormattedMessage id="farms.global.deposit.depositButton" />
            )}
          </Button>
        </div>
      </div>
      <div>
        <div style={{ paddingTop: 12 }}>
          <Label small style={{ color: colors.shade80 }}>
            <FormattedMessage id="farms.list.apy" />
          </Label>
          <Label small>
            {farm.apy || '...'}
            {farm.apy === '...' || !farm.apy ? '' : '%'}
          </Label>
        </div>
        <div>
          <Label small style={{ color: colors.shade80 }}>
            <FormattedMessage id="farms.list.deposits" />
          </Label>
          <Label small secondary={nothingStaked}>
            <AmountFiat
              amount={lpTokenValue(readableStakedBalance, pair, tokenRate)}
              currency="USD"
              locale="en-US"
            />
          </Label>
        </div>
        <div>
          <Label small style={{ color: colors.shade80 }}>
            <FormattedMessage id="farms.global.rewards" />
          </Label>
          <Label small secondary={nothingStaked}>
            <Coin
              alt={reward.value}
              isErc
              altSrc={reward.icon}
              ticker={reward.ticker}
              size="16px"
            />
            <AmountCrypto
              amount={earnedAmount}
              blockchain={reward.value}
              decimals={earnedAmount >= 1000 ? 0 : 2}
              locale="en-US"
            />
          </Label>
        </div>
      </div>
    </MobileRowButton>
  );
};

export default MobileRow;
